import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDirection } from "../../hooks/useDirection";
import {
  MapPin,
  Phone,
  Mail,
  Facebook,
  Twitter,
  Linkedin,
  Youtube,
} from "lucide-react";

const Footer = () => {
  const { t, i18n } = useTranslation("common");
  const direction = useDirection();
  const currentLang = i18n.language || "ar";

  // الترجمات للفوتر
  const footerTranslations = {
    ar: {
      quickLinks: "روابط سريعة",
      contactUs: "تواصل معنا",
      location: " جوزيف تيتو - الهايكستب- القاهرة",
      phone: "02-21816946",
      email: "nspo@nspo.com.eg",
      rights: "© جميع الحقوق محفوظة. تم تنفيذ هذا الموقع بواسطة فرع النظم بجهاز مشروعات الخدمة الوطنية.",
      followUs: "تابعنا",
      organizationName: "جهاز مشروعات الخدمة الوطنية",
      organizationDesc:
        "نعمل معاً لبناء مستقبل أفضل من خلال المشروعات الوطنية الاستراتيجية التي تخدم الوطن والمواطن.",
      sectors: "القطاعات",
      projects: "المشاريع",
      news: "الأخبار",
      media: "المركز الإعلامي",
      careers: "الوظائف",
      investors: "المستثمرون",
      home: "الرئيسية",
      about: "نبذة عنا",
    },
    en: {
      quickLinks: "Quick Links",
      contactUs: "Contact Us",
      location: "Cairo, Egypt",
      phone: "+20 2 1234 5678",
      email: "nspo@nspo.com.eg",
      rights:
        "© All rights reserved. This website was implemented by the Systems Division of the National Service Projects Organization.",
      followUs: "Follow Us",
      organizationName: "National Service Projects Organization",
      organizationDesc:
        "Working together to build a better future through strategic national projects that serve the nation and its citizens.",
      sectors: "Sectors",
      projects: "Projects",
      news: "News",
      media: "Media Center",
      careers: "Careers",
      investors: "Investors",
      home: "Home",
      about: "About Us",
    },
    fr: {
      quickLinks: "Liens Rapides",
      contactUs: "Contactez-Nous",
      location: "Le Caire, Égypte",
      phone: "+20 2 1234 5678",
      email: "info@nspo.eg",
      rights:
        "© Tous droits réservés. Ce site a été réalisé par la Division des Systèmes de l’Organisation des Projets de Service National.",
      followUs: "Suivez-Nous",
      organizationName: "Organisation des Projets de Service National",
      organizationDesc:
        "Travailler ensemble pour construire un avenir meilleur grâce à des projets nationaux stratégiques qui servent la nation et ses citoyens.",
      sectors: "Secteurs",
      projects: "Projets",
      news: "Actualités",
      media: "Centre Média",
      careers: "Carrières",
      investors: "Investisseurs",
      home: "Accueil",
      about: "À Propos",
    },
  };

  const tr = footerTranslations[currentLang] || footerTranslations.ar;

  return (
    <footer
      className="relative bg-gray-900 text-white overflow-hidden"
      dir={direction}
    >
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* القسم العلوي */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          {/* معلومات المؤسسة */}
          <div className="md:col-span-2">
            <div className="flex items-center space-x-3 rtl:space-x-reverse mb-4">
              <div className="w-12 h-12 bg-primary-600 rounded-lg flex items-center justify-center">
                <img
                  src="/assets/images/NSPO-Logo.webp"
                  alt="NSPO Logo"
                  className="w-10 h-10 object-contain"
                />
              </div>
              <span className="text-2xl font-bold">{tr.organizationName}</span>
            </div>
            <p className="text-gray-400 max-w-md mb-6">{tr.organizationDesc}</p>

            {/* وسائل التواصل الاجتماعي */}
            <div className="flex items-center space-x-4 rtl:space-x-reverse">
              <span className="text-gray-400 font-medium">{tr.followUs}:</span>
              <div className="flex items-center space-x-3 rtl:space-x-reverse">
                <a
                  href="#"
                  className="w-10 h-10 bg-gray-800 hover:bg-primary-600 rounded-lg flex items-center justify-center transition-all duration-300 border border-gray-700"
                >
                  <Facebook size={18} className="text-white" />
                </a>
                <a
                  href="#"
                  className="w-10 h-10 bg-gray-800 hover:bg-primary-400 rounded-lg flex items-center justify-center transition-all duration-300 border border-gray-700"
                >
                  <Twitter size={18} className="text-white" />
                </a>
                <a
                  href="#"
                  className="w-10 h-10 bg-gray-800 hover:bg-primary-700 rounded-lg flex items-center justify-center transition-all duration-300 border border-gray-700"
                >
                  <Linkedin size={18} className="text-white" />
                </a>
                <a
                  href="#"
                  className="w-10 h-10 bg-gray-800 hover:bg-red-600 rounded-lg flex items-center justify-center transition-all duration-300 border border-gray-700"
                >
                  <Youtube size={18} className="text-white" />
                </a>
              </div>
            </div>
          </div>

          {/* روابط سريعة */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-white">
              {tr.quickLinks}
            </h3>
            <div className="space-y-3">
              <Link
                to="/"
                className="block text-gray-400 hover:text-white transition-colors duration-300"
              >
                {tr.home}
              </Link>
              <Link
                to="/about"
                className="block text-gray-400 hover:text-white transition-colors duration-300"
              >
                {tr.about}
              </Link>
              <Link
                to="/sectors"
                className="block text-gray-400 hover:text-white transition-colors duration-300"
              >
                {tr.sectors}
              </Link>
              <Link
                to="/projects"
                className="block text-gray-400 hover:text-white transition-colors duration-300"
              >
                {tr.projects}
              </Link>
              <Link
                to="/careers"
                className="block text-gray-400 hover:text-white transition-colors duration-300"
              >
                {tr.careers}
              </Link>
            </div>
          </div>

          {/* معلومات الاتصال */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-white">
              {tr.contactUs}
            </h3>
            <div className="space-y-4 text-gray-400">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center border border-gray-700">
                  <MapPin size={18} className="text-primary-400" />
                </div>
                <div>
                  <p className="text-white">{tr.location}</p>
                  <p className="text-sm text-gray-500">المقر الرئيسي</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center border border-gray-700">
                  <Phone size={18} className="text-primary-400" />
                </div>
                <div>
                  <p className="text-white">{tr.phone}</p>
                  <p className="text-sm text-gray-500">الهاتف</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center border border-gray-700">
                  <Mail size={18} className="text-primary-400" />
                </div>
                <div>
                  <p className="text-white">{tr.email}</p>
                  <p className="text-sm text-gray-500">البريد الإلكتروني</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* القسم السفلي */}
        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col lg:flex-row items-center justify-between space-y-4 lg:space-y-0">
            <p className="text-gray-400 text-center lg:text-right">
              {tr.rights}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
